import { QueryClient } from '@tanstack/react-query';
import type { Entity, InvalidationStrategy } from './types';
import { getEntityConfig } from './entity';

export type InvalidateOptions = {
  entity: Entity;
  /** Strategy for which query keys to invalidate. Default: 'auto' */
  invalidationStrategy?: InvalidationStrategy;
  /** Extra query keys to invalidate (each item is a query key array). Used when strategy is 'auto' or 'only_provided_query_keys'. */
  invalidateQueryKeys?: readonly (readonly string[])[];
};

/**
 * Invalidates React Query cache based on entity config and strategy.
 *
 * - `none`: no invalidation
 * - `only_provided_query_keys`: only invalidate keys from options.invalidateQueryKeys (requires invalidateQueryKeys to be passed)
 * - `only_default_query_keys`: only invalidate entity's default queryKeyPatterns from config
 * - `auto` (default): invalidate entity's default keys; if invalidateQueryKeys is passed, also invalidate those
 */
export const runInvalidateQueryKeys = (
  queryClient: QueryClient,
  options: InvalidateOptions,
): void => {
  const { entity, invalidationStrategy = 'auto', invalidateQueryKeys: providedKeys } = options;

  if (invalidationStrategy === 'none') {
    return;
  }

  const config = getEntityConfig(entity);
  const defaultKeys = config.queryKeyPatterns.map(key => [key] as readonly string[]);

  if (invalidationStrategy === 'only_provided_query_keys') {
    if (providedKeys?.length) {
      providedKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [...key] });
      });
    }
    return;
  }

  if (invalidationStrategy === 'only_default_query_keys') {
    defaultKeys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: [...key] });
    });
    return;
  }

  // 'auto': invalidate default keys, and merge with provided if any
  defaultKeys.forEach(key => {
    queryClient.invalidateQueries({ queryKey: [...key] });
  });
  if (providedKeys?.length) {
    providedKeys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: [...key] });
    });
  }
};
